class UsStorePopup extends ModalDialog {
  constructor() {
    super();
    this.init();
    this.attachEventListeners();
  }

  init() {
    this.delay = this.dataset.delay * 1000;
    this.customerCountry = this.dataset.customerCountry;
    this.cookieName = "us-store-popup-closed";
    this.rootDomain = "mickfanningsoftboards.com";
  }

  attachEventListeners() {
    // ModalDialog already wires the ModalClose (dismiss) link, overlay
    // click and ESC to hide(), which we override below — only the two
    // US-store buttons need their own listener.
    this.querySelectorAll(".js-us-redirect").forEach(button =>
      button.addEventListener("click", this.onRedirect.bind(this))
    );
  }

  // 30-day cookie on the shared root domain so the answer is remembered
  // across the Mick Fanning stores and the popup doesn't re-nag.
  // Falls back to a host-only cookie on preview/myshopify hosts.
  setClosedCookie() {
    const domain =
      window.location.hostname.indexOf(this.rootDomain) !== -1
        ? "; domain=." + this.rootDomain
        : "";
    const maxAge = 30 * 24 * 60 * 60;
    document.cookie =
      this.cookieName + "=true; path=/" + domain +
      "; max-age=" + maxAge + "; SameSite=Lax";
  }

  // Country from the browser timezone — 'us', 'au', or null (everywhere
  // else). Free, instant, no geo API. AU = Australia/*; US = the American
  // zones + Hawaii + the US/* aliases.
  detectCountry() {
    let tz = "";
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (error) {
      return null;
    }
    if (/^Australia\//.test(tz)) return "au";
    if (
      /^America\/(New_York|Detroit|Chicago|Menominee|Los_Angeles|Denver|Phoenix|Boise|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak)$/.test(tz) ||
      /^America\/(Indiana|Kentucky|North_Dakota)\//.test(tz) ||
      tz === "Pacific/Honolulu" ||
      /^US\//.test(tz)
    ) {
      return "us";
    }
    return null;
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
        this.show();
      }
      return;
    }

    // Never show to search-engine / AI crawlers (they render JS from US
    // datacenters and would otherwise see an interstitial on every page).
    if (/bot|crawl|spider|slurp|mediapartners|bingpreview|facebookexternalhit|embedly|quora|pinterest|whatsapp|telegram|slackbot|flipboard|tumblr|google|baidu|yandex|duckduck|semrush|ahrefs|lighthouse|headless|preview/i.test(navigator.userAgent || "")) {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    // ?us_popup=reset deletes the remembered answer, then the popup
    // behaves as if the visitor were brand new (for testers).
    if (params.get("us_popup") === "reset") {
      const domain =
        window.location.hostname.indexOf(this.rootDomain) !== -1
          ? "; domain=." + this.rootDomain
          : "";
      document.cookie =
        this.cookieName + "=; path=/" + domain + "; max-age=0; SameSite=Lax";
    }

    // ?us_popup=test force-shows regardless of cookie/timezone.
    const forceShow = params.get("us_popup") === "test";
    if (!forceShow) {
      if (getCookie(this.cookieName) === "true") return;
      // Signed-in customer's saved country wins over timezone.
      const country = this.customerCountry || this.detectCountry();
      // Only prompt visitors who appear to be in the US. Australians and
      // everyone else (and anyone we can't place) shop uninterrupted.
      if (country !== "us") return;
    }

    setTimeout(() => this.show(), this.delay);
  }

  show(opener) {
    // Hide Shopify chat bubble on mobile so it doesn't appear behind the modal.
    const chatBubble = document.getElementById("shopify-chat");
    if (chatBubble) {
      this.chatBubbleOriginalDisplay = window.getComputedStyle(chatBubble).display;
      chatBubble.style.display = "none";
    }
    super.show(opener);
  }

  // Any dismissal (close link, overlay click, ESC) remembers the answer.
  hide() {
    const chatBubble = document.getElementById("shopify-chat");
    if (chatBubble && this.chatBubbleOriginalDisplay) {
      chatBubble.style.display = this.chatBubbleOriginalDisplay;
    }
    this.setClosedCookie();
    super.hide();
  }

  onRedirect(event) {
    const button = event.currentTarget;
    this.setClosedCookie();
    if (!button.dataset.url) {
      this.hide();
      return;
    }
    const base = button.dataset.url.replace(/\/+$/, "");
    // coast=picked tells the destination store's coast-picker the visitor
    // already chose, so they aren't asked again on arrival.
    // utm_source tags the visit so it shows up as its own traffic source in
    // Shopify/GA reports (a plain custom param wouldn't). One param is
    // enough here — we don't split East vs West for this popup.
    const params = new URLSearchParams();
    params.set("coast", "picked");
    params.set("utm_source", "australia-location-popup");
    window.location.href = base + "/?" + params.toString();
  }
}

customElements.define("us-store-popup", UsStorePopup);
